import React, { useEffect, useState } from 'react';
import { supabaseAdmin } from '../utils/supabaseAdmin';
import { BarChart, Calendar, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface SignupStats {
  totalSignups: number;
  coupleSignups: number;
  vendorSignups: number;
  newsletterSubscriptions: number;
  recentSignups: Array<{
    name: string;
    email: string;
    type: 'couple' | 'vendor';
    created_at: string;
  }>;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<SignupStats>({
    totalSignups: 0,
    coupleSignups: 0,
    vendorSignups: 0,
    newsletterSubscriptions: 0,
    recentSignups: [],
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }

    async function fetchStats() {
      try {
        const [
          { count: totalSignups },
          { count: coupleSignups },
          { count: vendorSignups },
          { count: newsletterSubscriptions },
          { data: recentSignups },
        ] = await Promise.all([
          supabaseAdmin.from('signups').select('*', { count: 'exact' }),
          supabaseAdmin.from('signups').select('*', { count: 'exact' }).eq('type', 'couple'),
          supabaseAdmin.from('signups').select('*', { count: 'exact' }).eq('type', 'vendor'),
          supabaseAdmin.from('newsletter_subscriptions').select('*', { count: 'exact' }).eq('status', 'active'),
          supabaseAdmin.from('signups').select('name,email,type,created_at').order('created_at', { ascending: false }).limit(10),
        ]);

        setStats({
          totalSignups: totalSignups || 0,
          coupleSignups: coupleSignups || 0,
          vendorSignups: vendorSignups || 0,
          newsletterSubscriptions: newsletterSubscriptions || 0,
          recentSignups: recentSignups || [],
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <button
          onClick={() => supabaseAdmin.auth.signOut()}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Sign Out
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Total Signups"
          value={stats.totalSignups}
          description="Total registered users"
        />
        <StatCard
          icon={Users}
          title="Couples"
          value={stats.coupleSignups}
          description="Registered couples"
        />
        <StatCard
          icon={Users}
          title="Vendors"
          value={stats.vendorSignups}
          description="Registered vendors"
        />
        <StatCard
          icon={Calendar}
          title="Newsletter"
          value={stats.newsletterSubscriptions}
          description="Active subscribers"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Signups</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentSignups.map((signup, index) => (
                <tr key={index} className="border-b">
                  <td className="py-2">{signup.name}</td>
                  <td className="py-2">{signup.email}</td>
                  <td className="py-2 capitalize">{signup.type}</td>
                  <td className="py-2">
                    {new Date(signup.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: number;
  description: string;
}

function StatCard({ icon: Icon, title, value, description }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-primary-100 rounded-full">
          <Icon className="w-6 h-6 text-primary-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </div>
  );
}